import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdFacebook } from 'react-icons/md';
import { FaInstagram } from 'react-icons/fa';
import type { ScheduledCampaign } from './types';

interface CampaignDetailModalProps {
  campaign: ScheduledCampaign | null;
  isOpen: boolean;
  onClose: () => void;
  clickPosition?: { x: number; y: number } | null;
}

const CampaignDetailModal: React.FC<CampaignDetailModalProps> = ({ campaign, isOpen, onClose, clickPosition }) => {
  const [modalPosition, setModalPosition] = React.useState<{ top?: string; left?: string; right?: string; bottom?: string }>({});
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Calculate modal position based on click position
  React.useEffect(() => {
    if (!isOpen || !clickPosition || !modalRef.current) return;

    const modal = modalRef.current;
    const modalRect = modal.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const modalWidth = 320; // Fixed smaller width
    const modalHeight = modalRect.height;
    const spacing = 16;

    let position: { top?: string; left?: string; right?: string; bottom?: string } = {};

    // Calculate horizontal position
    const spaceOnRight = viewportWidth - clickPosition.x;
    const spaceOnLeft = clickPosition.x;

    if (spaceOnRight >= modalWidth + spacing) {
      position.left = `${clickPosition.x + spacing}px`;
    } else if (spaceOnLeft >= modalWidth + spacing) {
      position.right = `${viewportWidth - clickPosition.x + spacing}px`;
    } else {
      position.left = '50%';
    }

    // Calculate vertical position
    const spaceBelow = viewportHeight - clickPosition.y;
    const spaceAbove = clickPosition.y;

    if (spaceBelow >= modalHeight + spacing) {
      position.top = `${clickPosition.y + spacing}px`;
    } else if (spaceAbove >= modalHeight + spacing) {
      position.bottom = `${viewportHeight - clickPosition.y + spacing}px`;
    } else {
      position.top = '50%';
    }

    setModalPosition(position);
  }, [isOpen, clickPosition]);

  // Close modal on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!campaign) return null;

  const parseSocialMedia = (socialMedia: string): string[] => {
    try {
      if (socialMedia.startsWith('[')) {
        const parsed = JSON.parse(socialMedia);
        return Array.isArray(parsed) ? parsed : [socialMedia];
      }
      return socialMedia.split(',').map(p => p.trim());
    } catch {
      return socialMedia.split(',').map(p => p.trim());
    }
  };

  const getPlatformIcon = (platform: string) => {
    const upperPlatform = platform.toUpperCase();
    switch (upperPlatform) {
      case 'FACEBOOK':
        return <MdFacebook className="text-blue-500" size={14} />;
      case 'INSTAGRAM':
        return <FaInstagram className="text-pink-500" size={14} />;
      default:
        return null;
    }
  };

  const getPositionStyles = (): React.CSSProperties => {
    if (!clickPosition) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const style: React.CSSProperties = { position: 'fixed' };

    if (modalPosition.top) {
      style.top = modalPosition.top;
      if (modalPosition.left === '50%') {
        style.transform = 'translateX(-50%)';
      }
    }
    if (modalPosition.bottom) {
      style.bottom = modalPosition.bottom;
      if (modalPosition.left === '50%') {
        style.transform = 'translateX(-50%)';
      }
    }
    if (modalPosition.left && modalPosition.left !== '50%') {
      style.left = modalPosition.left;
    } else if (modalPosition.left === '50%') {
      style.left = '50%';
    }
    if (modalPosition.right) {
      style.right = modalPosition.right;
    }

    if (modalPosition.top === '50%' && modalPosition.left === '50%') {
      style.transform = 'translate(-50%, -50%)';
    }

    return style;
  };

  const socialMediaPlatforms = parseSocialMedia(campaign.social_media || '[]');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with minimal opacity - no blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/10 z-50"
          />

          {/* Modal with slide and scale animation */}
          <div className="fixed z-[60]" style={getPositionStyles()}>
            {/* Close button - outside modal */}
            <button
              onClick={onClose}
              className="absolute top-[-8px] right-[-8px] w-7 h-7 flex items-center justify-center rounded-full transition-colors z-[100] bg-orange-600 hover:bg-orange-700 shadow-lg"
              aria-label="Close modal"
            >
              <svg className="w-4 h-4 text-white font-bold" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal container */}
            <motion.div
              ref={modalRef}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="rounded-md w-80 max-h-[85vh] overflow-hidden"
              style={{
                backdropFilter: 'bg-[#EE4423]',
                boxShadow: '0 0 14px 15px  #ffffff08',
                background: '#212121',
                border: '1px solid #313131'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Content */}
              <div className="overflow-y-auto max-h-[85vh]">
              {/* Flyer Image */}
              {campaign.flyer_info?.flyer_url && (
                <div className="">
                  <img 
                    src={campaign.flyer_info.flyer_url} 
                    alt="Campaign Flyer" 
                    className="w-full h-auto object-cover object-top max-h-64"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/320x180/212121/EE4423?text=Flyer';
                    }}
                  />
                </div>
              )}

              <div className="p-3 space-y-3">
                {/* Duration */}
                <div>
                  {/* <h4 className="font-semibold mb-2 text-xs text-white">Duration</h4> */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-[9px] font-medium mb-1 text-orange-300">From</label>
                      <div className="text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200">
                        {new Date(campaign.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block text-[9px] font-medium mb-1 text-orange-300">To</label>
                      <div className="text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200">
                        {new Date(campaign.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Platforms and Status */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-[9px] font-medium mb-1 text-orange-300">Platforms</label>
                    <div className="flex gap-1">
                      {socialMediaPlatforms.map((platform: string, index: number) => (
                        <div key={index} className="flex items-center gap-1 px-2 py-1 rounded bg-gray-800 border border-orange-900/30">
                          {getPlatformIcon(platform)}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* <div>
                    <label className="block text-[9px] font-medium mb-1 text-orange-300">Status</label>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-medium ${
                      campaign.status === 'REQUESTED'
                        ? 'bg-yellow-900 text-yellow-200'
                        : campaign.status === 'COMPLETED'
                        ? 'bg-green-900 text-green-200'
                        : 'bg-gray-800 text-gray-300'
                    }`}>
                      {campaign.status || 'Scheduled'}
                    </span>
                  </div> */}
                </div>
              </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CampaignDetailModal;
